import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ICleaningSchedule from "../../../models/cleaningSchedule";
import { ILocalDetail } from "../../../models/requests/local/localDetail";

import {
  getEmployeesTimesheetOverview,
  getGlobalHoursByDay,
  getMiscleanedLocals,
  getMiscleanedLocalsDisplay,
  getPercentageChange,
  getProductivity,
  getTop10FloorsByWorkedHours,
  getTotalHours,
  getWorkedHoursByBuilding,
  getWorkedHoursRatioByBuilding,
  getWorkedHoursRatioByProfile,
} from "./dashboardTransformations";

import { IDashboardData } from "../../../models/globalDashboard/dashboardScan";
import CleaningScheduleService from "../../../services/local/CleaningScheduleService";
import hbscanStatService from "../../../services/stat/HBScanStatService";

interface IUseGlobalDashboardDataProps {
  propertyGroupId: number;
  start: Date;
  end: Date;
}

const useGlobalDashboardData = ({ propertyGroupId, start, end }: IUseGlobalDashboardDataProps) => {
  const { i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dashboardData, setDashboardData] = useState<IDashboardData | null>(null);
  const [cleaningSchedules, setCleaningSchedules] = useState<ICleaningSchedule[]>([]);
  const [locals, setLocals] = useState<ILocalDetail[]>([]);

  const previousIntervalStart = useMemo(() => {
    const daysDiff = Math.ceil(moment(end).diff(moment(start), "hours") / 24);
    return moment(start).subtract(daysDiff, "days").startOf("day").toDate();
  }, [start, end]);

  const previousIntervalEnd = useMemo(() => {
    return moment(start).subtract(1, "days").endOf("day").toDate();
  }, [start]);

  useEffect(() => {
    if (propertyGroupId > 0 && start && end) {
      getData();
    }
  }, [propertyGroupId, start, end, previousIntervalStart, previousIntervalEnd]);

  const getData = async () => {
    setIsLoading(true);
    try {
      const { data: dashboard } = await hbscanStatService.getDashboardData(
        propertyGroupId,
        start,
        end,
        previousIntervalStart,
        previousIntervalEnd
      );

      setDashboardData(dashboard);
      setLocals(dashboard.locals);

      const localIds = dashboard.locals.map(l => l.local.id);
      const cleaningSchedules = await CleaningScheduleService.GetCleaningSchedules(localIds);
      setCleaningSchedules(cleaningSchedules);
    } finally {
      setIsLoading(false);
    }
  };


  const currentInOutScans = dashboardData?.current.inOutScans ?? [];
  const previousInOutScans = dashboardData?.previous.inOutScans ?? [];
  const currentLocalScans = dashboardData?.current.propertyScans ?? [];
  const previousLocalScans = dashboardData?.previous.propertyScans ?? [];

  const { totalHours: globalHours, previousHours: previousGlobalHours } = useMemo(
    () => getTotalHours(currentInOutScans, previousInOutScans),
    [currentInOutScans, previousInOutScans]
  );

  const { totalHours: workedHours, previousHours: previousWorkedHours } = useMemo(
    () => getTotalHours(currentLocalScans, previousLocalScans),
    [currentLocalScans, previousLocalScans]
  );

  const globalPercentageChange = useMemo(
    () => getPercentageChange(globalHours, previousGlobalHours),
    [globalHours, previousGlobalHours]
  );

  const workedPercentageChange = useMemo(
    () => getPercentageChange(workedHours, previousWorkedHours),
    [workedHours, previousWorkedHours]
  );

  const productivity = useMemo(
    () => getProductivity(workedHours, globalHours),
    [workedHours, globalHours]
  );

  const productivityPercentageChange = useMemo(() => {
    const previousProductivity = getProductivity(previousWorkedHours, previousGlobalHours);
    return getPercentageChange(productivity, previousProductivity);
  }, [productivity, previousWorkedHours, previousGlobalHours]);

  const workedHoursByBuilding = useMemo(
    () => getWorkedHoursByBuilding(currentLocalScans),
    [currentLocalScans]
  );

  const globalHoursByDay = useMemo(
    () => getGlobalHoursByDay(currentInOutScans),
    [currentInOutScans, i18n.language]
  );

  const workedHoursRatioByBuilding = useMemo(
    () => getWorkedHoursRatioByBuilding(currentLocalScans),
    [currentLocalScans]
  );

  const miscleanedLocals = useMemo(
    () => getMiscleanedLocals(start, end, currentLocalScans, cleaningSchedules, locals),
    [start, end, currentLocalScans, cleaningSchedules, locals]
  );

  const miscleanedLocalsPercentageChange = useMemo(() => {
    const previous = getMiscleanedLocals(previousIntervalStart, previousIntervalEnd, previousLocalScans, cleaningSchedules, locals);
    return {
      nonVisitedLocals: getPercentageChange(miscleanedLocals.nonVisitedLocals.length, previous.nonVisitedLocals.length),
      insufficientlyCleanedLocals: getPercentageChange(miscleanedLocals.insufficientlyCleanedLocals.length, previous.insufficientlyCleanedLocals.length),
      overCleanedLocals: getPercentageChange(miscleanedLocals.overCleanedLocals.length, previous.overCleanedLocals.length),
    };
  }, [previousIntervalStart, previousIntervalEnd, previousLocalScans, cleaningSchedules, locals, miscleanedLocals]);

  const miscleanedLocalsDisplay = useMemo(() => ({
    nonVisitedLocals: getMiscleanedLocalsDisplay(miscleanedLocals.nonVisitedLocals),
    insufficientlyCleanedLocals: getMiscleanedLocalsDisplay(miscleanedLocals.insufficientlyCleanedLocals),
    overCleanedLocals: getMiscleanedLocalsDisplay(miscleanedLocals.overCleanedLocals),
  }), [miscleanedLocals, i18n.language]);

  const workedHoursRatioByProfile = useMemo(
    () => getWorkedHoursRatioByProfile(currentLocalScans),
    [currentLocalScans]
  );

  const top10FloorsByWorkedHours = useMemo(
    () => getTop10FloorsByWorkedHours(currentLocalScans),
    [currentLocalScans]
  );

  const employeesTimesheetOverview = useMemo(
    () => getEmployeesTimesheetOverview(
      { inOutScans: currentInOutScans, localScans: currentLocalScans },
      { inOutScans: previousInOutScans, localScans: previousLocalScans }
    ),
    [dashboardData]
  );

  return {
    isLoading,
    data: {
      globalHours,
      workedHours,
      globalPercentageChange,
      workedPercentageChange,
      productivity,
      productivityPercentageChange,
      workedHoursByBuilding,
      globalHoursByDay,
      workedHoursRatioByBuilding,
      miscleanedLocals,
      miscleanedLocalsPercentageChange,
      miscleanedLocalsDisplay,
      workedHoursRatioByProfile,
      top10FloorsByWorkedHours,
      employeesTimesheetOverview,
    },
  };
};

export default useGlobalDashboardData;
